import { ReactNode, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import CameraManager from '~/modules/camera/camera-manager';
import { Icon } from '~/modules/elements/akui/icon';
import Loader from '~/modules/elements/loader';
import MenuWithLogo, { PAGE_CARD } from '~/modules/elements/menu-with-logo';
import { MicIcon } from '~/modules/elements/mic-icon';
import { NavButton } from '~/modules/elements/nav-controls';
import useBackgroundMusic from '~/modules/hooks/use-background-music';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import useMobileModeDisabled from '~/modules/hooks/use-mobile-mode-disabled';
import useSmoothNavigate from '~/modules/hooks/use-smooth-navigate';
import { nextValue } from '~/modules/utils/indexes';
import { PanelLabel } from '~/routes/profiles/profile-panels';
import { DestinationTile, SettingsCard, SettingsRow, SettingsSection } from '~/routes/settings/settings-rows';
import {
  DebugDifficultiesSetting,
  FpsCount,
  FPSCountSetting,
  GraphicSetting,
  GraphicsLevel,
  InputLagSetting,
  IsCalibratedSetting,
  MobilePhoneModeSetting,
  useSettingValue,
} from '~/routes/settings/settings-state';
import { domNavProps } from '~/routes/singers/nav-props';

/**
 * The same family as Singers and the mic setup, rather than the 60rem column it used to sit in.
 * `bare` sets a 1.625rem gap; the card sits closer than that to the toolbar row above it.
 */
const SETTINGS_CARD = `${PAGE_CARD} gap-[1.125rem]`;

/**
 * Two columns: the settings you change, and the places you go. Four of the nine rows this screen
 * used to show were navigation dressed as switches, which is what the split fixes. A grid rather
 * than a wrapping flex row - a flex column cannot fill the line it wraps onto, it stays at its
 * basis and leaves a ragged half-width column.
 */
const SPLIT = 'min-[60rem]:[grid-template-columns:minmax(0,1fr)_25rem]';

function Settings() {
  useBackgroundMusic(false);
  const navigate = useSmoothNavigate();
  const goBack = () => navigate('menu/');

  const { register } = useKeyboardNav({ onBackspace: goBack, title: 'Settings' });

  const [graphicLevel, setGraphicLevel] = useSettingValue(GraphicSetting);
  const [fpsCount, setFpsCount] = useSettingValue(FPSCountSetting);
  const [mobilePhoneMode, setMobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const [debugDifficulties, setDebugDifficulties] = useSettingValue(DebugDifficultiesSetting);
  const [inputLag] = useSettingValue(InputLagSetting);
  const [isCalibrated] = useSettingValue(IsCalibratedSetting);
  const mobileModeDisabled = useMobileModeDisabled();

  // Collapsed, because its own comment in `settings-state.ts` says it is set from the console. It
  // should not be the first thing on the screen, and it should not be the fifth either.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [camera, setCamera] = useState<null | boolean>(CameraManager.getPermissionStatus());
  useEffect(() => {
    return CameraManager.addListener((status) => {
      setCamera(status);
    });
  }, []);

  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const enableCamera = () => {
    setIsRequestInProgress(true);
    CameraManager.requestPermissions().then(() => setIsRequestInProgress(false));
  };

  let cameraValue = 'disabled';
  let cameraDisplayValue: ReactNode = 'Disabled';
  if (isRequestInProgress) {
    cameraValue = 'Loading';
    cameraDisplayValue = <Loader />;
  } else if (camera === null) {
    cameraValue = 'Click to enable';
    cameraDisplayValue = 'Click to enable';
  } else if (camera) {
    cameraValue = 'Enabled';
    cameraDisplayValue = 'Enabled';
  }

  const graphicsProps = register(
    'graphics-level',
    () => setGraphicLevel(nextValue(GraphicsLevel, graphicLevel)),
    'Graphics',
    true,
    { control: { type: 'switch', label: 'Graphics', value: graphicLevel.toUpperCase() } },
  );
  const fpsProps = register('fps-count-level', () => setFpsCount(nextValue(FpsCount, fpsCount)), 'FPS Count', false, {
    control: { type: 'switch', label: 'FPS Count', value: String(fpsCount) },
  });
  const cameraProps = register(
    'camera-access',
    () => (camera ? CameraManager.disable() : enableCamera()),
    'Enable camera mode',
    false,
    { control: { type: 'switch', label: 'Camera mode', value: cameraValue } },
  );
  const mobileProps = register(
    'mobile-phone-mode',
    () => setMobilePhoneMode(!mobilePhoneMode),
    'Mobile Phone Mode',
    false,
    { control: { type: 'switch', label: 'Mobile Phone Mode', value: mobilePhoneMode ? 'Yes' : 'No' } },
  );
  const advancedProps = register('advanced-settings', () => setAdvancedOpen((open) => !open), 'Advanced', false, {
    control: { type: 'button', label: 'Advanced' },
  });
  // Registered only while open: a control the keyboard can reach but nobody can see is worse than
  // one that is simply not there yet.
  const debugProps = advancedOpen
    ? register('debug-difficulties', () => setDebugDifficulties(!debugDifficulties), 'Debug Difficulties', false, {
        control: { type: 'switch', label: 'Debug difficulties', value: debugDifficulties ? 'Yes' : 'No' },
      })
    : null;

  const micSetupProps = register('setup-mics-button', () => navigate('select-input/'), 'Setup Microphones', false, {
    control: { type: 'button', label: 'Setup Microphones' },
  });
  const remoteMicsProps = register(
    'remote-mics-settings',
    () => navigate('settings/remote-mics/'),
    'Remote Microphones Settings',
    false,
    { control: { type: 'button', label: 'Remote mics settings' } },
  );
  const manageSongsProps = register('manage-songs-button', () => navigate('manage-songs/'), 'Manage Songs', false, {
    control: { type: 'button', label: 'Manage Songs' },
  });
  const calibrationProps = register(
    'calibration-settings',
    () => navigate('settings/calibration/'),
    'Calibrate input lag',
    false,
    { control: { type: 'button', label: 'Calibrate input lag' } },
  );

  return (
    <MenuWithLogo spacing="bare" className={SETTINGS_CARD} data-test="settings-page">
      <Helmet>
        <title>Settings | AllKaraoke.Party - Free Online Karaoke Party Game</title>
      </Helmet>
      <KeyboardNavContext value={register}>
        <div className="flex flex-wrap items-center gap-[1.125rem]">
          <NavButton
            name="back-button"
            variant="back"
            size="small"
            className="h-[3.375rem] w-auto px-5"
            remoteLabel="Return To Main Menu"
            onClick={goBack}>
            ← Menu
          </NavButton>
          <span className="stroke-text typography text-active text-[2.125rem] font-bold">Settings</span>
        </div>

        <SettingsCard className={SPLIT}>
          <div className="flex min-w-0 flex-col gap-[1.375rem]">
            <SettingsSection label="Display">
              <SettingsRow
                navProps={graphicsProps}
                label="Graphics"
                value={graphicLevel.toUpperCase()}
                data-test-value={graphicLevel.toUpperCase()}
              />
              <SettingsRow navProps={fpsProps} label="FPS count" value={fpsCount} data-test-value={String(fpsCount)} />
            </SettingsSection>

            <SettingsSection label="Gameplay">
              <SettingsRow
                navProps={cameraProps}
                label="Camera mode"
                value={cameraDisplayValue}
                data-test-value={cameraValue}
                info="Record a timelapse video from singing. The recording is not sent nor stored anywhere."
              />
              {!mobileModeDisabled && (
                <SettingsRow
                  navProps={mobileProps}
                  label="Mobile phone mode"
                  value={mobilePhoneMode ? 'Yes' : 'No'}
                  data-test-value={mobilePhoneMode ? 'Yes' : 'No'}
                  info="Adjust the game to a smaller screen. Disables option to sing in duets."
                />
              )}
            </SettingsSection>

            <div className="flex flex-col gap-2.5 border-t border-white/10 pt-[1.125rem]">
              <button
                type="button"
                {...domNavProps(advancedProps)}
                className={`flex h-[2.625rem] cursor-pointer items-center justify-between gap-2.5 rounded ${
                  advancedProps.focused ? 'subtle-focus' : ''
                }`}>
                <PanelLabel>Advanced</PanelLabel>
                <span className="flex items-center gap-2.5 text-[0.9375rem] text-white/35">
                  1 setting
                  <Icon
                    icon={advancedOpen ? 'ic:baseline-keyboard-arrow-up' : 'ic:baseline-expand-more'}
                    width="1.25rem"
                    height="1.25rem"
                  />
                </span>
              </button>
              {debugProps && (
                <SettingsRow
                  navProps={debugProps}
                  label="Debug difficulties"
                  value={debugDifficulties ? 'Yes' : 'No'}
                  data-test-value={debugDifficulties ? 'Yes' : 'No'}
                  info="Adds three extra difficulty levels, wider than Easy, for watching how the scoring engine reacts."
                />
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-2.5">
            <PanelLabel>Go to</PanelLabel>
            <DestinationTile
              navProps={micSetupProps}
              label="Microphone setup"
              icon="ic:baseline-settings"
              glyph={<MicIcon className="fill-active size-[1.625rem] shrink-0" />}
            />
            <DestinationTile navProps={remoteMicsProps} label="Remote microphones" icon="ic:baseline-phone-android" />
            <DestinationTile navProps={manageSongsProps} label="Manage songs" icon="ic:baseline-list" />
            <DestinationTile
              navProps={calibrationProps}
              label="Input lag"
              note={isCalibrated ? `Calibrated · ${inputLag} ms` : 'Not calibrated'}
              icon="ic:baseline-speed"
            />
          </div>
        </SettingsCard>
        {/* Clears the keyboard hint strip fixed along the bottom. */}
        <div aria-hidden className="h-14" />
      </KeyboardNavContext>
    </MenuWithLogo>
  );
}

export default Settings;
